/**
 * Three brand variants — each a complete token set (fonts, palette,
 * treatment). BrandProvider writes the active variant onto :root as
 * CSS variables so every component reads through one indirection.
 *
 * The three names — Calm, Grounded, Bold — describe a deliberate
 * progression in voice strength. Calm is the softest; Bold is the
 * most pronounced. Pick by how loudly the surface wants to speak.
 */

export type VariantId = "calm" | "grounded" | "bold";

export interface BrandVariant {
  id: VariantId;
  name: string;
  oneLiner: string;
  longForm: string;
  fonts: {
    display: string;
    body: string;
    flair: string;
    devanagari: string;
    displayVariation?: string;
  };
  palette: {
    bg: string;
    bgSoft: string;
    paper: string;
    ink: string;
    inkSoft: string;
    amber: string;
    amberLight: string;
    peach: string;
    peachDeep: string;
    rule: string;
  };
  treatment: {
    radius: string;
    shadow: string;
    headlineWeight: number;
    headlineLetterSpacing: string;
    headlineLineHeight: number;
  };
}

export const VARIANTS = {
  calm: {
    id: "calm",
    name: "Calm",
    oneLiner: "Softer headlines, cream-led surfaces.",
    longForm:
      "Cormorant Garamond for headlines, Source Serif 4 for body, Caveat for the one piece of handwritten flair per surface. Cream sand on top, espresso for type, amber for italic emphasis. Use when the page should read like a quiet book rather than a piece of marketing.",
    fonts: {
      display: '"Cormorant Garamond", Georgia, serif',
      body: '"Source Serif 4", Georgia, serif',
      flair: '"Caveat", cursive',
      devanagari: '"Tiro Devanagari Sanskrit", "Noto Sans Devanagari", serif',
    },
    palette: {
      bg: "#efddb5",
      bgSoft: "#f5e6c3",
      paper: "#fbeed1",
      ink: "#2a1c12",
      inkSoft: "#4a3324",
      amber: "#b07a3e",
      amberLight: "#c89860",
      peach: "#ecc18a",
      peachDeep: "#d99a4d",
      rule: "rgba(42,28,18,0.18)",
    },
    treatment: {
      radius: "14px",
      shadow: "0 8px 32px rgba(42,28,18,0.16)",
      headlineWeight: 500,
      headlineLetterSpacing: "-0.018em",
      headlineLineHeight: 0.96,
    },
  },
  grounded: {
    id: "grounded",
    name: "Grounded",
    oneLiner: "The shipped brand — walnut sand, Fraunces, saffron italic.",
    longForm:
      "This is what the website, the meditation challenge, the admin app, and our transactional emails actually use today. Fraunces (variable, opsz 144, wght 600, SOFT 30) for display; Inter for body; Caveat for the 5% of flair. Walnut-sand background, ink for body, saffron italic for one weighted moment per surface. If you're making something new for Vihangam Yog Australia, start here.",
    fonts: {
      display: '"Fraunces", Georgia, serif',
      body: '"Inter", -apple-system, sans-serif',
      flair: '"Caveat", cursive',
      devanagari: '"Tiro Devanagari Sanskrit", "Noto Sans Devanagari", serif',
      displayVariation: '"opsz" 144, "wght" 600, "SOFT" 30',
    },
    palette: {
      bg: "#e6cf9a",
      bgSoft: "#efdcaf",
      paper: "#f7e6bf",
      ink: "#1c1108",
      inkSoft: "#3b2716",
      amber: "#a05a1f",
      amberLight: "#c97a2e",
      peach: "#e8b06a",
      peachDeep: "#d18434",
      rule: "rgba(28,17,8,0.22)",
    },
    treatment: {
      radius: "12px",
      shadow: "0 10px 36px rgba(28,17,8,0.22)",
      headlineWeight: 600,
      headlineLetterSpacing: "-0.022em",
      headlineLineHeight: 0.94,
    },
  },
  bold: {
    id: "bold",
    name: "Bold",
    oneLiner: "Sharper type, marigold accents — for catching attention.",
    longForm:
      "DM Serif Display for high-contrast display, DM Sans for body, Caveat for flair. Ivory background, coal text, marigold accents. Use for one-off pieces where the post or poster needs to compete in a busy feed — a fact card, a launch announcement, a poster in a window. Don't use across the website or product surfaces.",
    fonts: {
      display: '"DM Serif Display", Georgia, serif',
      body: '"DM Sans", -apple-system, sans-serif',
      flair: '"Caveat", cursive',
      devanagari: '"Tiro Devanagari Sanskrit", "Noto Sans Devanagari", serif',
    },
    palette: {
      bg: "#ead9b2",
      bgSoft: "#f3e6c4",
      paper: "#faedca",
      ink: "#1a1410",
      inkSoft: "#4a3d2e",
      amber: "#c47526",
      amberLight: "#de9a4a",
      peach: "#f0c386",
      peachDeep: "#e29844",
      rule: "rgba(26,20,16,0.22)",
    },
    treatment: {
      radius: "10px",
      shadow: "0 12px 40px rgba(26,20,16,0.24)",
      headlineWeight: 400,
      headlineLetterSpacing: "-0.025em",
      headlineLineHeight: 0.92,
    },
  },
} satisfies Record<VariantId, BrandVariant>;

export const DEFAULT_VARIANT: VariantId = "calm";
export const VARIANT_ORDER: VariantId[] = ["calm", "grounded", "bold"];

/**
 * Old IDs map to the new names. Keeps existing shareable URLs working
 * after the rename so anyone with a `?v=cathedral` link still lands.
 */
export const VARIANT_ALIASES = {
  "living-room": "calm",
  cathedral: "grounded",
  atelier: "bold",
} satisfies Record<string, VariantId>;
